import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { MessageToast, ToastMode } from "../components/shared/MessageToast";
import { WorkorderGrid } from "../components/workorders/WorkorderGrid";
import { WorkorderCreate } from "../components/workorders/WorkorderCreate";
import { WorkorderBulkCreateDialog } from "../components/workorders/BulkWorkorderCreation/WorkorderBulkCreateDialog";
import { Customer } from "../components/shared/interfaces/Customer";
import { CustomersByEntityDropdown } from "../components/CustomersByEntityDropdown";
import { Snackbar } from "../components/shared/Snackbar";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { useWorkordersWithAssignments } from "../components/data/Workorders/useAllWorkordersWithAssignments";
import { BulkWorkorderContextProvider } from "../components/workorders/BulkWorkorderCreation/BulkWorkorderContext";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../components/data/useSitesByCompanyId";
import { PersistedCompanyDropdown } from "../components/PersistedCompanyDropdown";
import { PersistedSiteDropdown } from "../components/PersistedSiteDropdown";
import { Stack } from "@mui/material";

export function Workorders() {
  const { t } = useTranslation();

  const [errorToast, setErrorToast] = useState({
    show: false,
    mode: ToastMode.Error,
    text: "",
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [workorderCreatePanelOpen, setWorkorderCreatePanelOpen] = useState(false);
  const [workorderBulkCreateDialogOpen, setWorkorderBulkCreateDialogOpen] = useState(false);

  // State variable indicating whether a customer has been selected or not.
  // Note: the 'All Customer' virtual Customer does not qualify as a selected Customer
  const [customerSelected, setCustomerSelected] = useState<boolean | "all">(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const workorders = useWorkordersWithAssignments(
    customerSelected === "all" ? "all" : selectedCustomer?.id,
    companyId,
    siteId,
  );

  useEffect(() => {
    if (workorders.error) {
      setErrorToast({
        text: t("common.dataFetchError", { val: "$t(workorders.workorders)" }),
        mode: ToastMode.Error,
        show: true,
      });
    } else {
      setErrorToast({
        text: "",
        mode: ToastMode.Error,
        show: false,
      });
    }
  }, [workorders.error, t]);

  const handleCustomerSelect = useCallback((customer?: Customer) => {
    setSelectedCustomer(customer);

    if (customer) setCustomerSelected(true);
    else setCustomerSelected(false);
  }, []);

  function handleCompanyChange(selectedCompanyName: string) {
    setSelectedCompanyName(selectedCompanyName);
  }

  function handleSiteChange(siteName: string) {
    setSelectedSiteName(siteName);
  }

  function handleNewWorkorderButtonClick() {
    setWorkorderCreatePanelOpen(true);
  }

  function handleNewWorkorderPanelClose() {
    setWorkorderCreatePanelOpen(false);
  }

  function handleWorkorderCreated(workorderId: number) {
    workorders.refetch();
    setSnackbarText(t("workorders.workorderCreated", { val: workorderId }));
    setShowSnackbar(true);
    setWorkorderCreatePanelOpen(false);
  }

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <Stack direction="row">
        <CustomersByEntityDropdown
          selectedCustomerId={selectedCustomer?.id}
          entity="workorder"
          onSelect={handleCustomerSelect}
        />
        <PersistedCompanyDropdown customerId={selectedCustomer?.id} onCompanyChange={handleCompanyChange} showLabel />
        <PersistedSiteDropdown
          companyId={companyId}
          customerId={selectedCustomer?.id}
          onSiteChange={handleSiteChange}
          showLabel
        />
      </Stack>

      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show}></MessageToast>
        </div>
      ) : (
        selectedCustomer && (
          <Box marginTop={2}>
            <WorkorderGrid
              workorderData={workorders.data}
              workorderDataLoading={workorders.isLoading}
              workorderDataFetching={workorders.isFetching}
              onDataRefreshClick={workorders.refetch}
              onNewWorkorderClick={handleNewWorkorderButtonClick}
              onBulkWorkorderCreateClick={() => setWorkorderBulkCreateDialogOpen(true)}
              selectedCustomerId={selectedCustomer.id}
              companyId={companyId}
              siteId={siteId}
            />
            {workorderCreatePanelOpen && (
              <WorkorderCreate
                customerSelected={Boolean(customerSelected)}
                open={workorderCreatePanelOpen}
                onClose={handleNewWorkorderPanelClose}
                customer={selectedCustomer}
                onWorkorderCreated={handleWorkorderCreated}
              />
            )}

            {workorderBulkCreateDialogOpen && (
              <BulkWorkorderContextProvider>
                <WorkorderBulkCreateDialog
                  open={workorderBulkCreateDialogOpen}
                  onClose={() => setWorkorderBulkCreateDialogOpen(false)}
                  customerSelected={Boolean(customerSelected)}
                  customer={selectedCustomer}
                />
              </BulkWorkorderContextProvider>
            )}
          </Box>
        )
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
        message={snackbarText}
      />
    </React.Fragment>
  );
}
